import React, { ChangeEvent, useMemo } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Typography,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { CourseEvaluation } from "../../../../services/courses/courses.types";

const { TextArea } = Input;
const { Text, Title } = Typography;

export const MAX_QUESTIONS = 20;

// Crea una pregunta vacía (una pregunta = un CourseEvaluation sin rúbricas)
export const createEmptyQuestion = (): CourseEvaluation => ({
  key_learnings: "",
  rubric_achieved: "",
  rubric_in_process: "",
  rubric_needs_support: "",
  axis: "",
  max_points: 1,
  tooltip: "",
});

interface CourseQuestionsFormProps {
  courseEvaluations: CourseEvaluation[];
  setCourseEvaluations: React.Dispatch<
    React.SetStateAction<CourseEvaluation[]>
  >;
  // Axis catalog for the current subject (SCRUM-740). Empty means the subject
  // has no axis catalog, so the per-question axis selector stays hidden.
  availableAxes: string[];
}

// Editor de "Configuración de preguntas" para cursos diagnósticos de Primaria:
// cada pregunta tiene puntaje máximo y texto de tooltip (concepto evaluado).
const CourseQuestionsForm: React.FC<CourseQuestionsFormProps> = ({
  courseEvaluations,
  setCourseEvaluations,
  availableAxes,
}) => {
  const totalPoints = useMemo(
    () =>
      courseEvaluations.reduce(
        (sum, question) => sum + (question.max_points ?? 0),
        0,
      ),
    [courseEvaluations],
  );

  const handleAddQuestion = () => {
    if (courseEvaluations.length >= MAX_QUESTIONS) return;
    setCourseEvaluations([...courseEvaluations, createEmptyQuestion()]);
  };

  const handleRemoveQuestion = (index: number) => {
    setCourseEvaluations(courseEvaluations.filter((_, i) => i !== index));
  };

  const showAxisSelector = availableAxes.length > 0;

  // "" ("Sin eje") is the default and matches how existing questions without
  // an axis are stored in the backend.
  const axisOptions = useMemo(
    () => [
      { value: "", label: "Sin eje" },
      ...availableAxes.map((axis) => ({ value: axis, label: axis })),
    ],
    [availableAxes],
  );

  const handleQuestionChange = (
    index: number,
    field: "max_points" | "tooltip" | "axis",
    value: number | string | null,
  ) => {
    setCourseEvaluations(
      courseEvaluations.map((question, i) =>
        i === index ? { ...question, [field]: value } : question,
      ),
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ marginBottom: 0 }}>
          Configuración de preguntas
        </Title>
        <Text type="secondary">
          {courseEvaluations.length}{" "}
          {courseEvaluations.length === 1 ? "pregunta" : "preguntas"} ·{" "}
          {totalPoints} pts totales
        </Text>
      </div>

      {showAxisSelector && (
        <Alert
          type="info"
          showIcon
          message="Sobre los ejes"
          description="Los ejes permiten analizar el desempeño por competencias y generar diagramas de araña. El porcentaje de cada eje se calcula como puntos obtenidos entre puntos posibles de las preguntas del eje."
          style={{ marginBottom: 16 }}
        />
      )}

      {courseEvaluations.map((question, index) => (
        <Card key={index} style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text strong>Pregunta {index + 1}</Text>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {showAxisSelector && (
                <>
                  <Text type="secondary">Eje (opcional)</Text>
                  {/* Deliberately NOT wrapped in a Form.Item with `name`:
                      rc-field-form would inject its own value and override
                      the controlled one (known repo gotcha). Controlled
                      directly by courseEvaluations, like the fields below. */}
                  <Select
                    value={question.axis ?? ""}
                    onChange={(value: string) =>
                      handleQuestionChange(index, "axis", value)
                    }
                    options={axisOptions}
                    style={{ width: 240 }}
                  />
                </>
              )}
              <InputNumber
                min={1}
                value={question.max_points ?? undefined}
                onChange={(value) =>
                  handleQuestionChange(index, "max_points", value)
                }
                style={{ width: 80 }}
              />
              <Text type="secondary">pts</Text>
              <Popconfirm
                title="¿Eliminar esta pregunta?"
                description="Esta acción no se puede deshacer"
                onConfirm={() => handleRemoveQuestion(index)}
                okText="Sí"
                cancelText="No"
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
          </div>

          <TextArea
            rows={3}
            placeholder="Texto del tooltip..."
            value={question.tooltip ?? ""}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              handleQuestionChange(index, "tooltip", e.target.value)
            }
          />
        </Card>
      ))}

      <div
        style={{
          border: "1px dashed var(--bo-border-strong)",
          borderRadius: 8,
          padding: 16,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          cursor:
            courseEvaluations.length >= MAX_QUESTIONS
              ? "not-allowed"
              : "pointer",
          opacity: courseEvaluations.length >= MAX_QUESTIONS ? 0.5 : 1,
        }}
        onClick={handleAddQuestion}
      >
        <PlusOutlined />
        <Typography.Text strong>Añadir pregunta</Typography.Text>
      </div>
    </div>
  );
};

export default CourseQuestionsForm;
